import React from "react";
import { CCol, CFormGroup, CInput, CLabel, CRow, CSelect } from "@coreui/react";
import { useAutoInvoiceFormContext } from "../context/AutoInvoiceFormContext";
import { FieldErrors } from "../../form/FieldErrors";
import { getFieldErrors } from "../../../models/errors";
import DepartmentSelect from "../../location/DepartmentSelect";
import DistrictSelect from "../../location/DistrictSelect";
import CitySelect from "../../location/CitySelect";
import {
  AUTO_INVOICE_PROVIDER_TYPE_CHOICES,
  AUTO_INVOICE_PROVIDER_DOCUMENT_TYPE_CHOICES,
} from "../../../models/auto-invoice";
import { PYG } from "../../../currency/available-currencies";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

const AutoInvoiceFormProviderSection: React.FC = () => {
  const {
    autoInvoiceRef,
    providerDepartment,
    providerDistrict,
    providerCity,
    errors,
    onProviderTypeChange,
    onProviderDocumentTypeChange,
    onProviderDocumentNumberChange,
    onProviderFullNameChange,
    onProviderAddressChange,
    onProviderHouseNumberChange,
    onProviderDepartmentChange,
    onProviderDistrictChange,
    onProviderCityChange,
  } = useAutoInvoiceFormContext();

  return (
    <>
      <SectionDivider icon="fa-user" label="Datos del Proveedor" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Tipo de proveedor:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CSelect
              type="text"
              defaultValue={
                autoInvoiceRef.current !== null
                  ? autoInvoiceRef.current.providerType
                  : PYG
              }
              onChange={onProviderTypeChange}
            >
              <option value={""} disabled>
                -----
              </option>
              {Array.from(AUTO_INVOICE_PROVIDER_TYPE_CHOICES.entries()).map(
                (entry) => {
                  return (
                    <option key={entry[0]} value={entry[0]}>
                      {entry[1]}
                    </option>
                  );
                }
              )}
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("providerType", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Tipo de Documento:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CSelect
              type="text"
              defaultValue={
                autoInvoiceRef.current !== null
                  ? autoInvoiceRef.current.providerDocumentType
                  : PYG
              }
              onChange={onProviderDocumentTypeChange}
            >
              <option value={""} disabled>
                -----
              </option>
              {Array.from(
                AUTO_INVOICE_PROVIDER_DOCUMENT_TYPE_CHOICES.entries()
              ).map((entry) => {
                return (
                  <option key={entry[0]} value={entry[0]}>
                    {entry[1]}
                  </option>
                );
              })}
            </CSelect>
            <FieldErrors
              errors={
                getFieldErrors("providerDocumentType", errors) as string[]
              }
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Nro. Documento:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CInput
              type="text"
              defaultValue={
                autoInvoiceRef.current !== null
                  ? autoInvoiceRef.current.providerDocumentNumber
                  : PYG
              }
              onChange={onProviderDocumentNumberChange}
              placeholder="Nro. Documento"
            />
            <FieldErrors
              errors={
                getFieldErrors("providerDocumentNumber", errors) as string[]
              }
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Nombre Proveedor:</FormLabel>
          </CCol>
          <CCol md={10}>
            <CInput
              type="text"
              defaultValue={
                autoInvoiceRef.current !== null
                  ? autoInvoiceRef.current.providerFullName
                  : PYG
              }
              onChange={onProviderFullNameChange}
              placeholder="Nombre Completo"
            />
            <FieldErrors
              errors={getFieldErrors("providerFullName", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <div style={{ display: "flex", alignItems: "center", margin: "12px 0 14px" }}>
        <div style={{ width: "3px", height: "14px", background: "#006657", borderRadius: "2px", marginRight: "8px", opacity: 0.5 }} />
        <span style={{ fontWeight: 600, fontSize: "0.75rem", color: "var(--theme-muted)", textTransform: "uppercase", letterSpacing: "0.6px" }}>
          Dirección del Proveedor
        </span>
        <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "8px" }} />
      </div>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Dirección:</FormLabel>
          </CCol>
          <CCol md={6}>
            <CInput
              type="text"
              defaultValue={
                autoInvoiceRef.current
                  ? autoInvoiceRef.current.providerAddress
                  : ""
              }
              onChange={onProviderAddressChange}
              placeholder="Dirección"
            />
            <FieldErrors
              errors={getFieldErrors("providerAddress", errors) as string[]}
            />
          </CCol>
          <CCol md={2}>
            <FormLabel required>Nro. de casa:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CInput
              type="number"
              defaultValue={
                autoInvoiceRef.current
                  ? autoInvoiceRef.current.providerHouseNumber
                  : undefined
              }
              onChange={onProviderHouseNumberChange}
              placeholder="Número de casa"
            />
            <FieldErrors
              errors={getFieldErrors("providerHouseNumber", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Departamento:</FormLabel>
          </CCol>
          <CCol md={2}>
            <DepartmentSelect
              onChange={onProviderDepartmentChange}
              value={providerDepartment}
              placeholder="Departamento"
            />
            <FieldErrors
              errors={getFieldErrors("providerDepartment", errors) as string[]}
            />
          </CCol>
          <CCol md={2}>
            <FormLabel required>Distrito:</FormLabel>
          </CCol>
          <CCol md={2}>
            <DistrictSelect
              onChange={onProviderDistrictChange}
              value={providerDistrict}
              placeholder="Distrito"
              departmentValue={
                providerDepartment !== null ? providerDepartment : undefined
              }
            />
            <FieldErrors
              errors={getFieldErrors("providerDistrict", errors) as string[]}
            />
          </CCol>
          <CCol md={2}>
            <FormLabel required>Ciudad:</FormLabel>
          </CCol>
          <CCol>
            <CitySelect
              onChange={onProviderCityChange}
              placeholder="Ciudad"
              value={providerCity}
              district={
                providerDistrict !== null ? providerDistrict : undefined
              }
              department={
                providerDepartment !== null ? providerDepartment : undefined
              }
            />
            <FieldErrors
              errors={getFieldErrors("providerCity", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};

export default AutoInvoiceFormProviderSection;
